import { useState } from "react";
import { Link } from "react-router-dom";
import { Arr } from "../../components/CTA";

/* ───────────────────────── Interactive simulation ─────────────────────── */

function StatusIcon({ s }) {
  if (s === "ok")
    return (
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="sim__icon sim__icon--ok">
        <path d="M3 8.5l3.2 3.2L13 4.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  if (s === "flag")
    return (
      <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="sim__icon sim__icon--flag">
        <path d="M4 14V3h7l-1.4 2.5L11 8H4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
    );
  return null;
}

function ResultRow({ k, v, c, status, note }) {
  return (
    <div className={`sim__row sim__row--${status}`}>
      <div className="sim__rk">{k}</div>
      <div className="sim__rv">
        <span className="sim__rval">{v}</span>
        {note && <span className="sim__rnote">{note}</span>}
      </div>
      <div className="sim__rc">
        <StatusIcon s={status} />
        <span className="sim__conf">{c}</span>
      </div>
    </div>
  );
}

function Simulation() {
  const [step, setStep] = useState(1);
  const [picked, setPicked] = useState(null);
  const [err, setErr] = useState(null);

  const submit = () => {
    if (!picked) return;
    if (picked === "wrong") {
      setErr("That is the subtotal. The total due is the larger figure. Take another look.");
      return;
    }
    setErr(null);
    setStep(2);
  };

  const reset = () => {
    setStep(1);
    setPicked(null);
    setErr(null);
  };

  return (
    <aside className="sim" aria-labelledby="sim-title" data-testid="review-simulation">
      <header className="sim__head">
        <h3 id="sim-title" className="sim__title">Be the reviewer for a moment</h3>
        <p className="sim__intro">
          One flagged field, one correction. Watch what it does to the next document.
        </p>
        <ol className="sim__stepbar" aria-label="Progress">
          <li className={step >= 1 ? "is-on" : ""}>1<span>Review</span></li>
          <li className={step >= 2 ? "is-on" : ""}>2<span>Loop</span></li>
          <li className={step >= 3 ? "is-on" : ""}>3<span>Next document</span></li>
        </ol>
      </header>

      <div className="sim__viewport" key={step} data-testid={`sim-step-${step}`}>
        {/* ─────────── STEP 1 ─────────── */}
        {step === 1 && (
          <div className="sim__panel">
            <div className="sim__panel-head">DOCUMENT 1 OF 2 &nbsp;·&nbsp; INVOICE &nbsp;·&nbsp; ACME SUPPLIES LTD</div>
            <div className="sim__rows">
              <ResultRow k="Invoice number" v="INV-20418" c="99%" status="ok" />
              <ResultRow k="Vendor" v="Acme Supplies Ltd" c="98%" status="ok" />
              <ResultRow k="Due date" v="30 April 2026" c="96%" status="ok" />
              <div className="sim__row sim__row--flag sim__row--active">
                <div className="sim__rk">Total amount</div>
                <div className="sim__rv">
                  <span className="sim__rval sim__rval--flag">Flagged for review</span>
                  <span className="sim__rnote">Low confidence, 64%. This document shows two amounts. Which one is the total?</span>
                </div>
                <div className="sim__rc">
                  <StatusIcon s="flag" />
                  <span className="sim__conf sim__conf--warn">64%</span>
                </div>
              </div>
            </div>

            {/* Source snippet */}
            <div className="sim__source">
              <div className="sim__source-head">From the document</div>
              <div className="sim__source-rows">
                <div><span>Subtotal</span><b>10,400.00</b></div>
                <div><span>VAT at 20%</span><b>2,080.00</b></div>
                <div className="sim__source-total"><span>Total due</span><b>12,480.00</b></div>
              </div>
            </div>

            {/* Options */}
            <div className="sim__options" role="radiogroup" aria-label="Choose the total amount">
              {[
                { id: "wrong", label: "GBP 10,400.00" },
                { id: "right", label: "GBP 12,480.00" },
              ].map((o) => (
                <button
                  key={o.id}
                  type="button"
                  role="radio"
                  aria-checked={picked === o.id}
                  className={`sim__opt${picked === o.id ? " is-picked" : ""}`}
                  onClick={() => { setPicked(o.id); setErr(null); }}
                  data-testid={`sim-option-${o.id}`}
                >
                  <span className="sim__opt-mark" aria-hidden="true" />
                  {o.label}
                </button>
              ))}
            </div>

            {err && <p className="sim__err" role="alert" data-testid="sim-err">{err}</p>}

            <div className="sim__actions">
              <button
                type="button"
                className="sim__btn sim__btn--primary"
                onClick={submit}
                disabled={!picked}
                data-testid="sim-submit"
              >
                Submit review <Arr />
              </button>
            </div>
          </div>
        )}

        {/* ─────────── STEP 2 ─────────── */}
        {step === 2 && (
          <div className="sim__panel sim__panel--loop">
            <div className="sim__panel-head">CORRECTION CAPTURED</div>
            <div className="sim__rows">
              <ResultRow k="Invoice number" v="INV-20418" c="99%" status="ok" />
              <ResultRow k="Vendor" v="Acme Supplies Ltd" c="98%" status="ok" />
              <ResultRow k="Due date" v="30 April 2026" c="96%" status="ok" />
              <ResultRow
                k="Total amount"
                v="GBP 12,480.00"
                c="confirmed by you"
                status="ok"
              />
            </div>

            <div className="sim__loop" aria-hidden="true">
              <div className="sim__loop-doc">
                <svg viewBox="0 0 18 22" fill="none"><path d="M3.5 1.5h9L17 6v15H3.5z" stroke="currentColor" strokeWidth="1.3"/><path d="M12.5 1.5V6H17" stroke="currentColor" strokeWidth="1.3"/></svg>
                <span>Your correction</span>
              </div>
              <svg className="sim__loop-arrow" viewBox="0 0 220 80" fill="none" preserveAspectRatio="xMidYMid meet">
                <path d="M30 50 C 60 -10, 160 -10, 190 50" stroke="#0E9AA6" strokeWidth="2" strokeLinecap="round" fill="none" strokeDasharray="320" strokeDashoffset="320">
                  <animate attributeName="stroke-dashoffset" from="320" to="0" dur=".9s" begin="0.1s" fill="freeze" />
                </path>
                <path d="M188 50 l 4 -8 M188 50 l 10 -1" stroke="#0E9AA6" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <div className="sim__loop-model">
                <svg viewBox="0 0 22 22" fill="none"><circle cx="5" cy="5" r="1.8" stroke="currentColor" strokeWidth="1.4"/><circle cx="17" cy="11" r="1.8" stroke="currentColor" strokeWidth="1.4"/><circle cx="5" cy="17" r="1.8" stroke="currentColor" strokeWidth="1.4"/><path d="M6.5 5h9M6.5 17h9M6 6l8 4M6 16l8-4" stroke="currentColor" strokeWidth="1.3"/></svg>
                <span>Model</span>
              </div>
            </div>

            <ul className="sim__lines">
              <li>Your correction did two things.</li>
              <li>It completed this document.</li>
              <li>And it became training signal for the model.</li>
            </ul>

            <p className="sim__bottom">
              A correction in Docket is not a patch. It is the model learning what you know.
            </p>

            <div className="sim__actions">
              <button type="button" className="sim__btn sim__btn--primary" onClick={() => setStep(3)} data-testid="sim-next">
                See the next document <Arr />
              </button>
            </div>
          </div>
        )}

        {/* ─────────── STEP 3 ─────────── */}
        {step === 3 && (
          <div className="sim__panel">
            <div className="sim__panel-head">DOCUMENT 2 OF 2 &nbsp;·&nbsp; INVOICE &nbsp;·&nbsp; ACME SUPPLIES LTD</div>
            <div className="sim__rows">
              <ResultRow k="Invoice number" v="INV-20461" c="99%" status="ok" />
              <ResultRow k="Vendor" v="Acme Supplies Ltd" c="98%" status="ok" />
              <ResultRow k="Total amount" v="GBP 9,360.00" c="95%" status="ok" />
              <ResultRow k="Due date" v="14 May 2026" c="97%" status="ok" />
            </div>

            <p className="sim__caption">
              Same field, same kind of document. The model is no longer unsure. What it learned from your one correction, it now reads on its own.
            </p>
            <p className="sim__close">
              That is the loop. Review is not the system failing. It is the system getting better, one correction at a time.
            </p>

            <div className="sim__actions sim__actions--end">
              <button type="button" className="sim__reset" onClick={reset} data-testid="sim-reset">
                Start over
              </button>
            </div>
          </div>
        )}
      </div>

      <p className="sim__foot">
        An illustration of the loop, simplified to two documents. In practice the model retrains on many corrections over time, and the gain is gradual rather than instant. The mechanism is real: every correction is training signal.
      </p>
    </aside>
  );
}

/* ────────────────────────────── Page ────────────────────────────── */

export default function ReviewIsAFeature() {
  return (
    <article className="article">
      <header className="article__head">
        <div className="container container--narrow">
          <nav className="crumb" aria-label="Breadcrumb">
            <Link to="/">Docket</Link> / <Link to="/insights">Insights</Link> / Review is a feature, not a failure
          </nav>
          <p className="article__eyebrow">Product &nbsp;·&nbsp; December 2025 &nbsp;·&nbsp; 4 min read</p>
          <h1 className="article__title">Review is a feature, not a failure</h1>
          <p className="article__standfirst">
            Why we route flagged work to a human instead of forcing a guess, and why every correction makes the next document easier.
          </p>
          <hr className="article__rule" />
        </div>
      </header>

      <div className="container container--narrow article__body">
        <p>
          Most document automation treats the moment a human has to step in as a small failure. The dashboards even say so. They show an exception rate, a percentage of documents that needed a person, and the unspoken instruction is to drive that number down. The lower it goes, the better the tool is supposed to be doing.
        </p>
        <p>
          We think that framing is wrong, and quietly expensive. So Docket is built on the opposite idea. Review is not the automation failing. Review is a designed part of how the system works, and it is one of the most valuable parts.
        </p>

        <h2 className="article__sub">What "review as failure" leads to</h2>
        <p>The framing matters, because a product built to be embarrassed by review will start making bad trades to avoid it.</p>
        <p>
          If every document that reaches a human counts against you, the way to look good is to send fewer documents to humans. Not by being more accurate, which is hard, but by lowering the bar: auto-accept more, hide low-confidence results, and when the model is unsure, return its best guess anyway rather than admit doubt.
        </p>
        <p>
          That is a clean exception rate bought with a dirty cost. Every guess that should have been a review is now a wrong value moving through your business with no flag on it. You did not remove the work. You converted a visible, cheap review into an invisible, expensive error. A tool that treats review as failure has a reason to guess, and that should worry you more than the exception rate ever did.
        </p>

        <h2 className="article__sub">What review actually is</h2>
        <p>Here is the same situation seen the other way.</p>
        <p>
          Every extracted field carries a confidence score. Most fields, on most documents, score high, and that work flows straight through to delivery untouched. A smaller set of fields score below the line you set. Those, and only those, go to a reviewer.
        </p>
        <p>
          That is not the automation breaking down. It is the automation doing something genuinely difficult and genuinely useful: identifying the precise slice of work where a human adds the most value, and spending human attention there and nowhere else. Your reviewer is not checking everything. They are checking the few things actually worth a second look, beside the source document, with the model's own uncertainty pointing them straight at it.
        </p>
        <p>
          A system that routes its uncertainty honestly is worth more than a system that hides it. Review is how Docket routes uncertainty honestly.
        </p>

        <Simulation />

        <h2 className="article__sub">The part that makes it a feature</h2>
        <p>There is one more thing, and it is the part that turns review from a cost into an asset.</p>
        <p>
          In a tool that treats review as failure, a correction is a dead end. The reviewer fixes the field, the document moves on, and the fix is forgotten. The next document with the same problem arrives just as wrong. The human effort is spent and gone.
        </p>
        <p>
          In Docket, a correction is training signal. When a reviewer fixes a flagged field, that fix does not just complete one document. It feeds back into the model. The field the model was unsure about, on that kind of document, is a field it is now better at. The reviewer is not patching a leak. They are teaching.
        </p>
        <p>
          That changes what review is. It is not a fixed tax you pay on every batch forever. It is an investment with a declining balance. The corrections your team makes early are the reason there is less to correct later, because the model has learned what they taught it. Validation and training are not two features. They are one loop.
        </p>

        <h2 className="article__sub">The honest version</h2>
        <p>
          Review will never reach zero, and it should not. Some documents are genuinely ambiguous, and on those you want a person's judgement, not a model's guess. The goal was never to remove the human from the loop.
        </p>
        <p>
          The goal was to spend the human well: only on the work that needs judgement, never on the work that does not, and in a way where what they decide is captured and reused instead of thrown away. A review queue that gets shorter over time is not a sign that the humans were beaten. It is a sign that the humans were listened to.
        </p>
        <p>
          That is what "a feature, not a failure" means. Not that review is unfortunate but tolerated. It is that review is the mechanism by which the whole system gets better.
        </p>

        {/* Closing CTA */}
        <section className="article__cta" data-testid="article-cta">
          <div>
            <h3>See the loop on your own documents.</h3>
            <p>A Docket pilot runs your real document types through the review loop. You will see the queue, make the corrections, and watch it get shorter, in four to six weeks.</p>
          </div>
          <Link to="/contact" className="article__cta-btn" data-testid="article-cta-btn">
            Request a pilot <Arr />
          </Link>
        </section>

        <div className="article__backwrap">
          <Link to="/insights" className="txtlink" data-testid="article-back">
            <svg className="arr" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ transform: "rotate(180deg)" }}>
              <path d="M3 8h9m0 0L8 4m4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to all Insights
          </Link>
        </div>
      </div>
    </article>
  );
}
